// Entry-edge mapper: `GET /action-banks/by-bank/:bankId` rows -> BankActionInterface.
//
// This endpoint is the one place the monitoring surface can get an action's
// bound command as an ENUM. It answers with plain JSON — no FlatBuffer
// converter is registered for it — and each row nests the action_catalog row it
// belongs to, which carries `cmd` (the Control member NAME) alongside
// `message_value` (its numeric value), plus the toggle pair.
//
// The bank relation (`GET /banks/:id` -> `data.actions`) cannot be relied on for
// the same job: nested GORM-preloaded relations only exist on the JSON path, so
// on the FlatBuffer path the actions array is simply absent. Reading the
// dedicated endpoint is what makes the monitoring list and the setup wizard's
// Actions table describe the same rows from the same source.
//
// Nothing here resolves a command to a catalog id — that stays in
// normalizeActionMessages, which runs on the output of this module. This file's
// only job is shape: snake_case wire row (with a camelCase twin, in case a
// converter is registered later) into the interface every consumer downstream
// already reads.
import { BankActionInterface, ActionCommandOption } from "../../../interfaces/Bank.interface";
import { asCommandOption } from "../actionButtons/actionButtonUtils";
import { MSG_TYPE_CONTROL } from "../../../customHooks/useSchemaMessages";
import { resolveActionButtonType, DEFAULT_ACTION_BUTTON_TYPE } from "./actionTypeConfig";
import formatUuidT from "../../../flatbuffers/display/uuid";

/**
 * Read a field under both spellings. The payload is snake_case today; the
 * camelCase probe costs nothing and is what every other boundary in this
 * codebase does, so a future FlatBuffer converter would not silently blank
 * every field.
 */
const dual = (source: unknown, camel: string, snake: string): unknown => {
  if (!source || typeof source !== "object") return undefined;
  const rec = source as Record<string, unknown>;
  return rec[camel] ?? rec[snake];
};

const obj = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

/**
 * The lift ids on a row's `lift_instances`, the per-car ActionCatalog rows that
 * share this row's command and bank. Absent on servers that predate the field,
 * which is why callers must treat empty as "bank-level", never as "no cars".
 */
const liftInstanceIds = (rec: Record<string, unknown>): string[] => {
  const raw = dual(rec, "liftInstances", "lift_instances");
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => formatUuidT(dual(obj(entry), "liftId", "lift_id") as string))
    .filter((id) => id.length > 0);
};

/** `undefined` for anything that is not a number, so a present 0 survives. */
const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

/**
 * The wire value meaning "this button binds no command".
 *
 * -1 rather than 0 because `Control` numbers its first member `CarCallLock = 0`,
 * so 0 is a legitimate command and cannot double as absent. See the contract on
 * `ActionBank.message_value`.
 */
export const ACTION_COMMAND_UNSET = -1;

/**
 * A command enum read off the ROW, with the unset sentinel mapped to absence.
 *
 * Reading -1 through as a number would hand every unbound button a command that
 * is not in the enum, and the panel would offer to send it. Absence is the
 * honest answer, and the button's own guard then refuses the press and says so.
 */
const boundCommand = (value: unknown): number | undefined => {
  const n = asNumber(value);

  return n === undefined || n === ACTION_COMMAND_UNSET ? undefined : n;
};

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

/** `undefined` for anything that is not a boolean, so absence stays absence. */
const asBool = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const asTextList = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : undefined;

const asNumberList = (value: unknown): number[] | undefined =>
  Array.isArray(value)
    ? value.filter((entry): entry is number => typeof entry === "number")
    : undefined;

/**
 * The catalog command option for one side of a row.
 *
 * A stored `command` / `toggle_command` object wins when the row carries one.
 * Otherwise it is rebuilt from the two fields that DO travel on every row:
 * `cmd` (the Control member name) and `message_value` (its enum). The label is
 * composed exactly as the setup wizard composes it, so a rebuilt option is
 * indistinguishable from a stored one.
 *
 * Absent when either half is missing — an option without a numeric cannot
 * dispatch, and one without a name cannot be displayed.
 *
 * It is also absent when the row's own category says the enum is not a Control
 * value. The rebuilt label asserts the category outright (`Control::<name>`), so
 * building one for a Traffic or Status binding manufactures the very claim the
 * resolver downstream refuses to make. Skipping the guard here is not academic:
 * `resolveCommandEnum` falls back to this option when the message id resolves to
 * nothing, which is exactly the state a refused binding is left in — so a
 * category-mismatched row would arrive at the button with a command anyway, and
 * press through to `Control::RunDown`.
 */
const commandOptionFor = (
  stored: unknown,
  commandName: unknown,
  numeric: number | undefined,
  contentType: number | null,
): ActionCommandOption | undefined => {
  const fromRow = asCommandOption(stored);
  if (fromRow) return fromRow;
  if (typeof commandName !== "string" || commandName === "" || numeric === undefined) {
    return undefined;
  }
  // 0/absent is "the server did not say", which every older server answers and
  // which must keep its previous behaviour. Only a stated, different category
  // blocks the rebuild.
  if (contentType !== null && contentType !== 0 && contentType !== MSG_TYPE_CONTROL) {
    return undefined;
  }
  return { label: `Control::${commandName}`, value: commandName, numeric };
};

/**
 * One `/action-banks/by-bank` row as a BankActionInterface, or null when the
 * row cannot be identified at all.
 *
 * Field precedence is "the action_bank row first, its action_catalog relation
 * second". The row owns the visual configuration (color, timers, and its own
 * copy of label / valid_states, which the operator edits per bank); the catalog
 * owns the command binding. Where both carry a field, the row is the one the
 * setup wizard's Visual Config modal writes, so it wins.
 */
export const actionBankRowToBankAction = (row: unknown): BankActionInterface | null => {
  if (!row || typeof row !== "object") return null;

  const rec = obj(row);
  const action = obj(rec.action);
  const message = obj(rec.message);
  const toggleMessage = obj(dual(rec, "toggleMessage", "toggle_message"));

  const bankId = formatUuidT(
    (dual(rec, "bankId", "bank_id") ?? dual(action, "bankId", "bank_id")) as string,
  );
  const actionId = formatUuidT((dual(rec, "actionId", "action_id") ?? action.id) as string);
  const messageId = formatUuidT(
    (dual(rec, "messageId", "message_id") ??
      dual(action, "messageId", "message_id") ??
      message.id) as string,
  );
  const toggleMessageId = formatUuidT(
    (dual(rec, "toggleMessageId", "toggle_message_id") ?? toggleMessage.id) as string,
  );
  const toggleActionId = formatUuidT(dual(rec, "toggleActionId", "toggle_action_id") as string);

  // Which car this row was configured for. action_bank is bank-scoped, so the
  // only per-lift signal on the endpoint is `lift_id` on the nested catalog row,
  // and its ABSENCE is meaningful: a row without one applies to every lift in
  // the bank (that is what SeedBankControlActions writes, and what the wizard
  // shows as "Bank-Level"). A consumer must therefore read empty as "all lifts",
  // never as "no lift" — the second reading hides every hall call.
  const liftId = formatUuidT(
    (dual(action, "liftId", "lift_id") ?? dual(rec, "liftId", "lift_id")) as string,
  );

  // action_bank is a composite-key entity (message_id, bank_id) with no id
  // column of its own. The catalog row id is 1:1 with it and is what the rest
  // of the UI already keys cards by, so prefer it; the composite key is the
  // fallback so a row still renders (and stays stable across refetches) when
  // the relation was not preloaded.
  const id = actionId || (messageId && bankId ? `${messageId}:${bankId}` : "");
  if (!id) return null;

  // NOTE the collision: `action.cmd` on the wire is the Control member NAME
  // ("CloseFrontDoor"), while `BankActionInterface.cmd` is its NUMERIC enum.
  // The number lives in `message_value`; reading `action.cmd` into `cmd` would
  // put a string where every consumer expects a Control value.
  //
  // The ROW is consulted last and matters most. `action` and `message` are
  // relations, and the packed transport emits no nested tables, so on that path
  // both are `{}` and neither of the first two sources can answer — which is how
  // every button on the panel came to report "no command bound" at once. The row
  // now carries the enum itself; see ActionBank.message_value.
  //
  // The enum is read TOGETHER WITH ITS CATEGORY, from one source. An enum value
  // means nothing without the category it belongs to, and the LN2 categories are
  // separate enums that collide numerically — 27 is both
  // `Traffic::IndependentService` and `Control::RunDown`. The two used to be
  // read independently: the enum from whichever of the three sources answered
  // first, the category only ever from the ROW. A server that sends the joined
  // `message` relation but no row-level `message_content_type` therefore
  // delivered "enum 27, category unknown", which the resolver reads as "assume
  // Control" — and the row came out addressing `Control::RunDown`. Pairing them
  // means an unknown category is a source that genuinely did not say, rather
  // than one we failed to ask.
  const commandPair = (
    joined: Record<string, unknown>,
    enumKeys: readonly [string, string],
    contentTypeKeys: readonly [string, string],
  ): { command: number | undefined; contentType: number | null } => {
    const fromCatalog = asNumber(dual(action, ...enumKeys));
    if (fromCatalog !== undefined) {
      return {
        command: fromCatalog,
        contentType: asNumber(dual(action, ...contentTypeKeys)) ?? null,
      };
    }

    const fromJoined = asNumber(dual(joined, "enumId", "enum_id"));
    if (fromJoined !== undefined) {
      return {
        command: fromJoined,
        contentType: asNumber(dual(joined, "messageContentType", "message_content_type")) ?? null,
      };
    }

    return {
      command: boundCommand(dual(rec, ...enumKeys)),
      contentType: asNumber(dual(rec, ...contentTypeKeys)) ?? null,
    };
  };

  const primaryCommand = commandPair(
    message,
    ["messageValue", "message_value"],
    ["messageContentType", "message_content_type"],
  );
  const toggleCommandPair = commandPair(
    toggleMessage,
    ["toggleMessageValue", "toggle_message_value"],
    ["toggleMessageContentType", "toggle_message_content_type"],
  );

  const commandEnum = primaryCommand.command;
  const toggleCommandEnum = toggleCommandPair.command;

  // The row's button type, resolved the way the setup wizard's Actions table
  // resolves it — including the SWITCH default.
  //
  // Two things were wrong with reading it as raw text. `asText` blanks a NUMBER,
  // and `button_type` travels as the ActionButtonType ordinal on the typed path,
  // so those rows arrived with an empty type. And an empty type was fatal
  // downstream: the Actions list drops every row whose type does not resolve, so
  // a row the wizard table showed as SWITCH had no card at all. Defaulting here,
  // at the one boundary both surfaces read, is what keeps them describing the
  // same rows — the wizard already defaults, so anything else guarantees the two
  // lists differ.
  const buttonType =
    resolveActionButtonType(dual(rec, "buttonType", "button_type")) ??
    resolveActionButtonType(dual(action, "buttonType", "button_type")) ??
    DEFAULT_ACTION_BUTTON_TYPE;

  return {
    id,
    bankId,
    actionId: actionId || undefined,
    toggleActionId: toggleActionId || undefined,
    messageId,
    toggleMessageId,
    label: asText(rec.label || action.label),
    toggleLabel: asText(
      dual(rec, "toggleLabel", "toggle_label") || dual(action, "toggleLabel", "toggle_label"),
    ),
    color: asText(rec.color),
    toggleColor: asText(dual(rec, "toggleColor", "toggle_color")),
    timer: asNumber(rec.timer) ?? 0,
    toggleTimer: asNumber(dual(rec, "toggleTimer", "toggle_timer")) ?? 0,
    validStates:
      asTextList(dual(rec, "validStates", "valid_states")) ??
      asTextList(dual(action, "validStates", "valid_states")) ??
      [],
    toggleValidStates:
      asTextList(dual(rec, "toggleValidStates", "toggle_valid_states")) ??
      asTextList(dual(action, "toggleValidStates", "toggle_valid_states")) ??
      [],

    // The messages whose arrival confirms the press worked. Read exactly like
    // valid_states — the action_bank row first, the joined catalog row as the
    // wizard-level default — because that is the relationship the schema
    // declares between the two copies. `[]` for a row that carries neither, and
    // an empty list is the honest answer: nothing is nominated, so a press can
    // only ever report "sent, unverifiable".
    expectedResponse:
      asTextList(dual(rec, "expectedResponse", "expected_response")) ??
      asTextList(dual(action, "expectedResponse", "expected_response")) ??
      [],
    // Seconds. 0 and absent both mean UNSET — the consumer applies its own
    // window — so `asNumber` (which preserves a present 0) is exactly right and
    // a `|| undefined` here would be a lie about which of the two we were told.
    expectedResponseTimeout:
      asNumber(dual(rec, "expectedResponseTimeout", "expected_response_timeout")) ??
      asNumber(dual(action, "expectedResponseTimeout", "expected_response_timeout")),

    // The actions-enum contract fields. normalizeActionMessageIds reads these
    // to rewrite messageId / validStates into catalog ids.
    cmd: commandEnum ?? null,
    // The category the enum belongs to, so a consumer never has to assume one.
    // Absent/0 means the server did not say; see BankActionInterface. Read from
    // the SAME source as the enum beside it — see commandPair.
    messageContentType: primaryCommand.contentType,
    toggleMessageContentType: toggleCommandPair.contentType,
    toggleCmd: toggleCommandEnum ?? null,

    // Whether the bound command takes a floor, straight off the joined Message
    // row (`messages.position`). The endpoint preloads Message, so this travels
    // on every JSON row already; `undefined` when the server did not send it,
    // which callers must read as "fall back to the enum classifier" rather than
    // as "no floor".
    takesPosition: asBool(message.position) ?? asBool(dual(rec, "takesPosition", "position")),
    // The server's floor verdict, from the bound command's KEY encoding. Wins
    // over takesPosition when present, because it answers the question the
    // operator actually configured rather than a column that is true almost
    // everywhere.
    // Which drawer the server says this button belongs in, from the bound
    // command's KEY encoding. Declared and read by actionLiftScope since drawer
    // routing was written, but never projected here — so serverScopeOf saw
    // undefined for every row and the routing silently did nothing.
    actionScope: asNumber(dual(rec, "actionScope", "action_scope")),
    floorScope: asNumber(dual(rec, "floorScope", "action_floor_scope")),
    command:
      commandOptionFor(
        rec.command ?? action.command,
        action.cmd,
        commandEnum,
        primaryCommand.contentType,
      ) ?? null,
    toggleCommand:
      commandOptionFor(
        dual(rec, "toggleCommand", "toggle_command") ??
          dual(action, "toggleCommand", "toggle_command"),
        dual(action, "toggleCmd", "toggle_cmd"),
        toggleCommandEnum,
        toggleCommandPair.contentType,
      ) ?? null,
    validServices:
      asNumberList(dual(rec, "validServices", "valid_services")) ??
      asNumberList(dual(action, "validServices", "valid_services")),
    toggleValidServices:
      asNumberList(dual(rec, "toggleValidServices", "toggle_valid_services")) ??
      asNumberList(dual(action, "toggleValidServices", "toggle_valid_services")),

    buttonType,
    // `visibleActions` sorts on this with localeCompare, so it has to be a
    // string even for a row the API sent without one.
    createdAt: asText(
      dual(rec, "createdAt", "created_at") || dual(action, "createdAt", "created_at"),
    ),
    updatedAt: asText(dual(rec, "updatedAt", "updated_at")) || undefined,
    deletedAt: (dual(rec, "deletedAt", "deleted_at") ??
      dual(action, "deletedAt", "deleted_at") ??
      null) as string | null,

    // Every car this command was configured for. by-bank preloads only the
    // ONE catalog `action_id` points at, so this is the sole signal that a
    // per-car command covers cars 2..N at all. Empty means bank-level, which
    // applies to every lift — see actionLiftScope.
    liftIds: liftInstanceIds(rec),

    // The catalog relation, carried through for its `liftId` alone — the
    // control panel scopes its Actions list to one car with it, and it remains
    // the fallback for a server that predates `lift_instances`.
    action: { liftId: liftId || null },
  };
};

/**
 * Every row of an `/action-banks/by-bank` response, tolerating both the
 * `{ data: [...] }` envelope and a bare array. Rows that cannot be identified
 * are dropped rather than rendered as a card with no command behind it.
 */
export const actionBankRowsToBankActions = (payload: unknown): BankActionInterface[] => {
  const raw = Array.isArray(payload) ? payload : obj(payload).data;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(actionBankRowToBankAction)
    .filter((action): action is BankActionInterface => action !== null);
};
